import { api, devzappApi } from '@/services/api'
import { onAuthStateChanged, signInWithCustomToken, signOut, User } from 'firebase/auth'
import { _auth, firebaseConfig } from '@/helpers/firebase'
import { ImpersonationSession, ImpersonationUser, ImpersonationUsersPage } from './types'

type StartFirebaseResponse = {
  customToken: string
  actorRestoreCustomToken: string
  session: { id: string; expiresAt: number }
  user: ImpersonationUser
}
type StartOauthResponse = { access_token: string; session_id: string; expires_in: number }
export const AUTH_SERVER_SESSION_REQUIRED = 'authserver_session_required'
export const FIREBASE_PROJECT_MISMATCH = 'firebase_project_mismatch'

function assertCustomTokenProject(token: string) {
  try {
    const encoded = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')))
    const expectedIssuerSuffix = `@${firebaseConfig.projectId}.iam.gserviceaccount.com`
    if (!firebaseConfig.projectId || !String(payload.iss || '').endsWith(expectedIssuerSuffix)) throw new Error()
  } catch {
    throw new Error(FIREBASE_PROJECT_MISMATCH)
  }
}

function assertAuthserverToken(token?: string) {
  try {
    const encoded = String(token).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')))
    if (String(payload.iss || '').includes('securetoken.google.com') || !payload.client_id) throw new Error()
  } catch {
    throw new Error(AUTH_SERVER_SESSION_REQUIRED)
  }
}

async function actorFirebaseToken(fallback: string) {
  let fallbackUid = ''
  try {
    const encoded = String(fallback).split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    const payload = JSON.parse(atob(encoded.padEnd(Math.ceil(encoded.length / 4) * 4, '=')))
    fallbackUid = String(payload.user_id || payload.sub || '')
  } catch {
    // The backend remains responsible for validating the token.
  }
  if (_auth?.currentUser && _auth.currentUser.uid === fallbackUid) {
    return await _auth.currentUser.getIdToken(true)
  }
  if (fallback) return fallback
  const restoredUser = await new Promise<User | null>(resolve => {
    let unsubscribe = () => undefined
    const timer = window.setTimeout(() => {
      unsubscribe()
      resolve(null)
    }, 1500)
    unsubscribe = onAuthStateChanged(
      _auth,
      user => {
        window.clearTimeout(timer)
        unsubscribe()
        resolve(user)
      },
      () => {
        window.clearTimeout(timer)
        unsubscribe()
        resolve(null)
      }
    )
  })
  if (!restoredUser) throw new Error('firebase_auth_session_not_available')
  return await restoredUser.getIdToken(true)
}

export async function searchImpersonationUsers(firebaseToken: string, query: string, page: number, pageSize = 10) {
  const token = await actorFirebaseToken(firebaseToken)
  const response = await api.get<ImpersonationUsersPage>('/impersonation/users', {
    params: { q: query, page, pageSize },
    headers: { Authorization: `Bearer ${token}` }
  })
  return response.data
}

export async function startImpersonation(
  originalUser: ImpersonationSession['originalUser'],
  target: ImpersonationUser,
  campaignId: string
) {
  assertAuthserverToken(originalUser.token)
  let firebaseToken = originalUser.accessToken
  let firebaseSessionId = ''
  let firebaseResponse: { data: StartFirebaseResponse } | undefined
  let oauthResponse: { data: StartOauthResponse } | undefined
  try {
    firebaseToken = await actorFirebaseToken(originalUser.accessToken)
    firebaseResponse = await api.post<StartFirebaseResponse>(
      '/impersonation/sessions',
      { targetUserId: target.id, campaignId },
      { headers: { Authorization: `Bearer ${firebaseToken}` } }
    )
    firebaseSessionId = firebaseResponse.data.session.id
    const authserverUserId = firebaseResponse.data.user.authserverUserId || target.devzappUserId
    if (!authserverUserId) throw new Error('authserver_owner_not_found')
    oauthResponse = await devzappApi.post<StartOauthResponse>(
      '/tokencampanha/impersonate/support',
      { userId: authserverUserId, campaignId },
      { headers: { Authorization: `Bearer ${originalUser.token}` } }
    )
    assertCustomTokenProject(firebaseResponse.data.customToken)
    const credential = await signInWithCustomToken(_auth, firebaseResponse.data.customToken)
    return {
      firebase: firebaseResponse.data,
      oauth: oauthResponse.data,
      firebaseIdToken: await credential.user.getIdToken(true),
      actorFirebaseToken: firebaseToken
    }
  } catch (error) {
    await Promise.allSettled([
      firebaseSessionId
        ? api.delete(`/impersonation/sessions/${firebaseSessionId}`, {
            headers: { Authorization: `Bearer ${firebaseToken}` }
          })
        : Promise.resolve(),
      oauthResponse
        ? devzappApi.post(
            '/tokencampanha/impersonate/end',
            {},
            { headers: { Authorization: `Bearer ${oauthResponse.data.access_token}` } }
          )
        : Promise.resolve(),
      firebaseResponse ? signInWithCustomToken(_auth, firebaseResponse.data.actorRestoreCustomToken) : Promise.resolve()
    ])
    throw error
  }
}

export async function endImpersonation(session: ImpersonationSession) {
  let oauthEndError: unknown
  try {
    await devzappApi.post(
      '/tokencampanha/impersonate/end',
      {},
      { headers: { Authorization: `Bearer ${session.simulatedUser.token}` } }
    )
  } catch (error) {
    oauthEndError = error
  }

  let actorToken = ''
  try {
    assertCustomTokenProject(session.actorRestoreCustomToken)
    const credential = await signInWithCustomToken(_auth, session.actorRestoreCustomToken)
    actorToken = await credential.user.getIdToken(true)
  } catch (error) {
    await signOut(_auth)
    throw error
  }

  let firebaseEndError: unknown
  try {
    await api.delete(`/impersonation/sessions/${session.firebaseSessionId}`, {
      headers: { Authorization: `Bearer ${actorToken}` }
    })
  } catch (error) {
    firebaseEndError = error
  }

  if (oauthEndError || firebaseEndError) throw oauthEndError || firebaseEndError
  return actorToken
}

export async function clearImpersonatedFirebaseAuth() {
  await signOut(_auth)
}
